import type { ComponentChildren } from "preact"
import { useEffect, useRef } from "preact/hooks"
import type { QuestionImage } from "../types"
import { BoardShell } from "./BoardShell"

interface QuestionBoardProps {
	// Status line content (question counter, scores) shown above the prompt.
	header: ComponentChildren
	prompt: string
	image: QuestionImage | null
	choices: string[]
	// Button variant for choice i — the caller owns it, because it is what
	// paints the reveal ("success" / "danger") over the resting "outline".
	// Must return a variant: the board adds none.
	choiceClass: (i: number) => string
	// When true the options are inert: no clicks, no number keys.
	disabled: boolean
	onAnswer: (choice: number) => void
	// Optional content under the options (waiting notices, errors).
	footer?: ComponentChildren
}

// Number-key shortcuts only make sense where there is a real keyboard. A
// coarse pointer (phone/tablet) gets the plain buttons with no key hints.
const hasKeyboard =
	typeof window !== "undefined" &&
	window.matchMedia("(hover: hover) and (pointer: fine)").matches

// QuestionBoard is the play surface for a four-choice question — every topic
// but buildnumbers — and is shared by solo rounds and matches. It is
// BoardShell (status line, prompt+image half) plus its own answer area: the
// options as one big full-width target per line, thumb-reachable on a phone
// and 1-4 keys on a desktop. See SpellBoard for the other kind.
export function QuestionBoard({
	header,
	prompt,
	image,
	choices,
	choiceClass,
	disabled,
	onAnswer,
	footer,
}: QuestionBoardProps) {
	// Kept in a ref so the listener below doesn't have to re-subscribe (and
	// callers don't have to memoize) every render.
	const answerRef = useRef(onAnswer)
	answerRef.current = onAnswer

	useEffect(() => {
		if (!hasKeyboard || disabled) return
		const handler = (event: KeyboardEvent) => {
			if (event.ctrlKey || event.metaKey || event.altKey) return
			const i = Number(event.key) - 1
			if (!Number.isInteger(i) || i < 0 || i >= choices.length) return
			event.preventDefault()
			answerRef.current(i)
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [choices.length, disabled])

	return (
		<BoardShell header={header} prompt={prompt} image={image} footer={footer}>
			<div class="board-options">
				{choices.map((choice, i) => (
					<button
						type="button"
						key={i}
						// The vertical padding is in .board-options button so the
						// landscape media query can shrink it. A disabled button is
						// dimmed by default; the same rule keeps it at full opacity so
						// the reveal colours are not washed out.
						class={choiceClass(i)}
						disabled={disabled}
						// Answer on touch-down, not on release. `click` only fires
						// once the finger comes back up, so waiting for it spends the
						// entire press on nothing — the most noticeable part of the
						// delay on a phone, and in a match it is time on the clock,
						// since answer_ms is stamped when the request reaches the
						// server.
						onPointerDown={(event) => {
							if (event.button !== 0) return
							onAnswer(i)
						}}
						// Keyboard activation (Enter/Space) produces a click with no
						// pointerdown in front of it, so this stays. After a real tap
						// it is a no-op: answering marks the board disabled, and a
						// disabled button dispatches no click.
						onClick={() => onAnswer(i)}
					>
						{hasKeyboard && <span class="board-key tnum">{i + 1}</span>}
						<span class="grow text-center">{choice}</span>
						{/* Mirrors the key hint's width so the label stays centered. */}
						{hasKeyboard && <span class="shrink-0" style="width: 1.6rem;" />}
					</button>
				))}
			</div>
		</BoardShell>
	)
}
